/**
 * Thin promise wrapper around IndexedDB. Three stores:
 *  - events: all tracked events, keyed by id
 *  - kv:     profile, settings, device info, pairing state
 *  - outbox: sync payloads not yet accepted by the relay
 */
import type { SyncPayload, TrackedEvent } from './types';

const DB_NAME = 'first-weeks';
const DB_VERSION = 1;

export interface OutboxEntry {
  /** event id, or 'profile' for profile snapshots (latest wins) */
  id: string;
  payload: SyncPayload;
  queuedAt: string;
  /**
   * Monotonic revision assigned on every put. Push cleanup deletes a row
   * only when its rev still matches the pushed snapshot, so an entry
   * re-queued while a push is in flight is never lost. Optional because
   * rows written by older builds lack it.
   */
  rev?: number;
}

let dbPromise: Promise<IDBDatabase> | null = null;

export function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains('events')) {
        const events = db.createObjectStore('events', { keyPath: 'id' });
        events.createIndex('at', 'at');
      }
      if (!db.objectStoreNames.contains('kv')) {
        db.createObjectStore('kv');
      }
      if (!db.objectStoreNames.contains('outbox')) {
        db.createObjectStore('outbox', { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

function reqResult<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function getAllEvents(): Promise<TrackedEvent[]> {
  const db = await openDb();
  const tx = db.transaction('events', 'readonly');
  return reqResult(tx.objectStore('events').getAll() as IDBRequest<TrackedEvent[]>);
}

export async function putEvents(events: TrackedEvent[]): Promise<void> {
  if (events.length === 0) return;
  const db = await openDb();
  const tx = db.transaction('events', 'readwrite');
  const store = tx.objectStore('events');
  for (const e of events) store.put(e);
  await txDone(tx);
}

export async function getEvent(id: string): Promise<TrackedEvent | undefined> {
  const db = await openDb();
  const tx = db.transaction('events', 'readonly');
  return reqResult(tx.objectStore('events').get(id) as IDBRequest<TrackedEvent | undefined>);
}

export async function kvGet<T>(key: string): Promise<T | undefined> {
  const db = await openDb();
  const tx = db.transaction('kv', 'readonly');
  return reqResult(tx.objectStore('kv').get(key) as IDBRequest<T | undefined>);
}

export async function kvSet(key: string, value: unknown): Promise<void> {
  const db = await openDb();
  const tx = db.transaction('kv', 'readwrite');
  tx.objectStore('kv').put(value, key);
  await txDone(tx);
}

export async function kvDelete(key: string): Promise<void> {
  const db = await openDb();
  const tx = db.transaction('kv', 'readwrite');
  tx.objectStore('kv').delete(key);
  await txDone(tx);
}

export async function outboxAll(): Promise<OutboxEntry[]> {
  const db = await openDb();
  const tx = db.transaction('outbox', 'readonly');
  return reqResult(tx.objectStore('outbox').getAll() as IDBRequest<OutboxEntry[]>);
}

export async function outboxPut(entry: OutboxEntry): Promise<void> {
  const db = await openDb();
  const tx = db.transaction('outbox', 'readwrite');
  tx.objectStore('outbox').put(entry);
  await txDone(tx);
}

/**
 * Delete outbox rows, but only when the stored rev still matches the pushed
 * snapshot. Read-check-delete runs inside one readwrite transaction, so a
 * row re-queued (newer rev) during the push round-trip survives to be
 * pushed again.
 */
export async function outboxDeleteMatching(
  rows: { id: string; rev: number | undefined }[],
): Promise<void> {
  if (rows.length === 0) return;
  const db = await openDb();
  const tx = db.transaction('outbox', 'readwrite');
  const store = tx.objectStore('outbox');
  for (const { id, rev } of rows) {
    const req = store.get(id) as IDBRequest<OutboxEntry | undefined>;
    req.onsuccess = () => {
      if (req.result && req.result.rev === rev) store.delete(id);
    };
  }
  await txDone(tx);
}

/** Drop every queued payload (used when unpairing, so history never leaks into a future pair). */
export async function outboxClear(): Promise<void> {
  const db = await openDb();
  const tx = db.transaction('outbox', 'readwrite');
  tx.objectStore('outbox').clear();
  await txDone(tx);
}

/** Wipe everything (used by "Delete all data" in settings). */
export async function wipeDb(): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(['events', 'kv', 'outbox'], 'readwrite');
  tx.objectStore('events').clear();
  tx.objectStore('kv').clear();
  tx.objectStore('outbox').clear();
  await txDone(tx);
}
